// Shared date-range rules used by every start/end picker across the app
// (customer search, lead forms, tenant forms, parking, etc.).
//
// Rules codified here:
//   • Start default: blank for new records/searches.
//   • End default: blank — picking a start opens the end picker instead of
//     pre-filling it (`applyStartChangeNoFill`). Legacy native inputs still
//     use `applyStartChange`, which fills start + 1 day.
//   • Minimum stay: end must be at least 1 day after start.
//     Opt-in `allowSameDay` relaxes this to "end >= start" (tenant admin override).
//   • User picks an end earlier than start → the picked date becomes the new
//     start and end jumps to start + 1.
//   • Changing start when the existing end is now invalid → end jumps to
//     start + 1.
//   • Opening an existing record never mutates its saved dates — the helpers
//     only ever run as a response to user input.
//
// The helpers are pure (no React) so native <input type="date"> consumers,
// react-day-picker consumers, and future pickers can all share them.

export const todayIso = () => {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

export const addDaysIso = (iso, n) => {
  if (!iso) return '';
  const [y, m, d] = iso.split('-').map(Number);
  const base = new Date(y, m - 1, d);
  base.setDate(base.getDate() + n);
  const yyyy = base.getFullYear();
  const mm = String(base.getMonth() + 1).padStart(2, '0');
  const dd = String(base.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

// Smallest allowed value for `end` given a chosen `start`.
export const minEndIso = (startIso, allowSameDay = false) => {
  if (!startIso) return '';
  return allowSameDay ? startIso : addDaysIso(startIso, 1);
};

// Default "next day" value callers can pre-fill into the end input so the
// native date picker opens to the start's month and that cell is highlighted.
export const defaultEndIso = (startIso) => (startIso ? addDaysIso(startIso, 1) : '');

// Apply the rules when the user edits the START date. Returns the new pair.
export const applyStartChange = (newStart, prevEnd, { allowSameDay = false } = {}) => {
  if (!newStart) return { start: '', end: prevEnd };
  if (!prevEnd) return { start: newStart, end: defaultEndIso(newStart) };
  const min = minEndIso(newStart, allowSameDay);
  if (prevEnd < min) return { start: newStart, end: defaultEndIso(newStart) };
  return { start: newStart, end: prevEnd };
};

// Apply the rules when the user edits the START date WITHOUT auto-filling the
// end (popover pickers: the end calendar opens next so the user picks it).
// A still-valid end is preserved; one that is now too early is cleared.
export const applyStartChangeNoFill = (newStart, prevEnd, { allowSameDay = false } = {}) => {
  if (!newStart) return { start: '', end: prevEnd };
  if (!prevEnd) return { start: newStart, end: '' };
  return prevEnd < minEndIso(newStart, allowSameDay)
    ? { start: newStart, end: '' }
    : { start: newStart, end: prevEnd };
};

// Apply the rules when the user edits the END date. Returns the new pair.
// Picking an end before the current start swaps: new start = picked date,
// new end = start + 1.
export const applyEndChange = (prevStart, newEnd, { allowSameDay = false } = {}) => {
  if (!newEnd) return { start: prevStart, end: '' };
  if (!prevStart) return { start: '', end: newEnd };
  const min = minEndIso(prevStart, allowSameDay);
  if (newEnd < min) return { start: newEnd, end: defaultEndIso(newEnd) };
  return { start: prevStart, end: newEnd };
};
